// Where a wrong address most likely meant to go.
//
// People type this site's URLs from memory, from a radio spot or a flyer, and they type
// what the page does ("tick-bite", "identify", "map") rather than what it is called.
// Every one of those used to land on an almost empty "Guide not found". This module
// maps the likely guesses to the right page, and is only ever consulted for an address
// that matched nothing, so it can never shadow a real page.
//
// Pure and dependency-light on purpose: the server entry calls it before rendering, the
// guide route calls it from its loader, and the not-found page calls suggestPath() for
// a "did you mean" line.

import { STATE_ABBR_TO_NAME } from "./states-index";

/** The site's real top-level pages, with the words a reader would recognise them by. */
export const MAIN_PAGES = [
  { path: "/tick-bite-tool", label: "What to do after a tick bite" },
  { path: "/tick-identification", label: "Identify a tick" },
  { path: "/map", label: "Lone Star tick map" },
  { path: "/prevention", label: "Prevent tick bites" },
  { path: "/alpha-gal", label: "Alpha-gal syndrome" },
  { path: "/states", label: "Every state" },
  { path: "/ticks", label: "Tick species" },
  { path: "/diseases", label: "Tick-borne diseases" },
  { path: "/resources", label: "Articles" },
  { path: "/tick-bite-guide", label: "Printable tick bite guide" },
  { path: "/sources", label: "Sources" },
  { path: "/about", label: "About this guide" },
  { path: "/contact", label: "Contact" },
] as const;

const MAIN_SLUGS = new Set<string>([
  ...MAIN_PAGES.map((p) => p.path.slice(1)),
  "privacy",
  "terms",
  "cookies",
  "disclaimer",
]);

/** One normalised slug -> the page it means. Keys are written the way normalise() leaves them. */
const ALIASES: Record<string, string> = {};
function alias(target: string, ...slugs: string[]) {
  for (const s of slugs) ALIASES[s] = target;
}

alias(
  "/tick-bite-tool",
  "tick-bite",
  "tick-bites",
  "tickbite",
  "bite",
  "bites",
  "bitten",
  "i-was-bitten",
  "i-got-bitten",
  "got-bitten",
  "was-bitten",
  "bite-tool",
  "tick-bite-tools",
  "tick-bite-help",
  "tick-bite-what-to-do",
  "what-to-do-after-a-tick-bite",
  "after-a-tick-bite",
  "after-tick-bite",
  "found-a-tick",
  "i-found-a-tick",
  "tick-attached",
  "remove-tick",
  "remove-a-tick",
  "tick-removal",
  "how-to-remove-a-tick",
  "bite-log",
);
alias(
  "/tick-identification",
  "identify",
  "identify-tick",
  "identify-a-tick",
  "identify-ticks",
  "identification",
  "tick-id",
  "tickid",
  "tick-identify",
  "tick-identifier",
  "tick-identification-tool",
  "id",
  "what-tick-is-this",
  "which-tick",
  "what-kind-of-tick",
);
alias(
  "/map",
  "maps",
  "tick-map",
  "ticks-map",
  "tickmap",
  "lone-star-map",
  "lonestar-map",
  "lone-star-tick-map",
  "lone-star-ticks-map",
  "range-map",
  "tick-range",
  "us-map",
  "usa-map",
  "interactive-map",
  "county-map",
);
alias("/alpha-gal", "alphagal", "alpha-gal-syndrome", "ags", "meat-allergy", "red-meat-allergy");
alias("/prevention", "prevent", "prevent-tick-bites", "repellent", "repellents", "protect");
alias("/states", "state", "states-list", "all-states", "by-state");
alias("/ticks", "species", "tick-species", "ticks-list", "types-of-ticks");
alias("/diseases", "disease", "tickborne-diseases", "tick-borne-diseases", "illnesses");
alias("/resources", "articles", "guides", "blog", "learn");
alias("/sources", "source", "references", "citations");
alias("/about", "about-us");
alias("/contact", "contact-us");
alias("/privacy", "privacy-policy");
alias("/terms", "terms-of-service", "terms-of-use");

/**
 * Pages folded into a fuller page on the same question. Two pages answering one search
 * split its links and its ranking, and readers landed on whichever was thinner; each of
 * these now sends readers, and search engines, to the one that stays.
 */
export const CONSOLIDATED: Readonly<Record<string, string>> = {
  "/resources/tick-season-by-state": "/tick-season-by-state",
  "/resources/where-lone-star-ticks-live-us-range-map": "/where-do-lone-star-ticks-live",
  "/resources/tick-safety-for-hikers": "/tick-safety-for-hikers",
  "/resources/tick-safety-for-hunters": "/tick-safety-for-hunters",
  "/resources/tick-proof-your-yard": "/tick-proof-yard",
  "/resources/ticks-on-dogs-prevention-removal": "/ticks-on-dogs",
  "/resources/alpha-gal-syndrome-symptoms": "/alpha-gal-symptoms",
  "/resources/how-to-identify-a-lone-star-tick": "/tick-identification",
  "/resources/tick-bite-action-plan": "/tick-bite-guide",
  "/lone-star-tick-identification": "/tick-identification",
  "/what-is-alpha-gal-syndrome": "/alpha-gal",
  "/ehrlichiosis-lone-star-tick": "/diseases/ehrlichiosis",
  "/southern-tick-associated-rash-illness-stari": "/diseases/stari",
};

const STATE_SLUGS = new Map<string, string>();
for (const [abbr, name] of Object.entries(STATE_ABBR_TO_NAME)) {
  const slug = name.toLowerCase().replace(/\s+/g, "-");
  STATE_SLUGS.set(slug, slug);
  STATE_SLUGS.set(abbr.toLowerCase(), slug);
  STATE_SLUGS.set(slug.replace(/-/g, ""), slug);
}
STATE_SLUGS.set("washington-dc", "district-of-columbia");
STATE_SLUGS.set("dc", "district-of-columbia");
STATE_SLUGS.set("washington-d-c", "district-of-columbia");

/**
 * Lowercase, decode, and turn spaces, underscores and pluses into hyphens. Drops a file
 * extension and any trailing slash, so "/Tick_Bite.html/" and "/tick bite" both become
 * "tick-bite".
 */
export function normalise(path: string): string {
  let p = path;
  try {
    p = decodeURIComponent(p);
  } catch {
    // A malformed escape is still worth matching on its other characters.
  }
  return p
    .toLowerCase()
    .split(/[?#]/)[0]
    .replace(/^\/+|\/+$/g, "")
    .replace(/\.(html?|php|aspx?)$/, "")
    .replace(/[\s_+]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/\/{2,}/g, "/");
}

/** The canonical state slug for something a reader typed ("ga", "new_york", "newyork"). */
export function stateSlugFor(input: string): string | undefined {
  return STATE_SLUGS.get(normalise(input));
}

/**
 * Where an unmatched address should redirect, or null to show the not-found page.
 * Only call this for an address that matched no route.
 */
export function resolveRedirect(path: string): string | null {
  const target = guess(normalise(path));
  // Never send an address to itself: that is a redirect loop, not an answer.
  const asGiven = path.split(/[?#]/)[0];
  return target && target !== asGiven ? target : null;
}

function guess(p: string): string | null {
  if (!p) return null;
  const moved = CONSOLIDATED[`/${p}`];
  if (moved) return moved;
  const segments = p.split("/");
  const [first, second] = segments;

  // "/Map", "/Tick_Identification.html": a real page, written another way.
  if (segments.length === 1 && MAIN_SLUGS.has(first)) return `/${first}`;

  // "/tick-bite", "/identify", "/lone star tick map"
  if (segments.length === 1 && ALIASES[first]) return ALIASES[first];

  // "/georgia", "/ga", "/new_york"
  if (segments.length === 1 && STATE_SLUGS.has(first)) return `/states/${STATE_SLUGS.get(first)}`;

  // "/states/ga", "/state/new-york", "/states/New_York" -> the state page
  if ((first === "states" || first === "state") && second && STATE_SLUGS.has(second)) {
    if (segments.length === 2) return `/states/${STATE_SLUGS.get(second)}`;
    return null;
  }

  // "/tick-bite/what-now", "/identify/lone-star": a task word followed by anything.
  if (segments.length > 1 && ALIASES[first] && first !== "state") return ALIASES[first];

  return null;
}

/** Edit distance, for "did you mean". Small inputs only. */
function distance(a: string, b: string): number {
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const up = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = up;
    }
  }
  return prev[b.length];
}

/**
 * The closest real page to a mistyped address ("/tick-identfication", "/georgai"), for
 * a "did you mean" line on the not-found page. Returns null unless the match is close.
 * `extra` adds pages this module does not know about, such as guide slugs.
 */
export function suggestPath(path: string, extra: readonly string[] = []): string | null {
  const p = normalise(path);
  if (!p || p.length > 60) return null;
  const candidates = new Map<string, string>();
  for (const page of MAIN_PAGES) candidates.set(page.path.slice(1), page.path);
  for (const [slug, target] of Object.entries(ALIASES)) candidates.set(slug, target);
  for (const [typed, slug] of STATE_SLUGS) candidates.set(typed, `/states/${slug}`);
  for (const e of extra) candidates.set(normalise(e), `/${normalise(e)}`);

  let best: { target: string; d: number } | null = null;
  for (const [key, target] of candidates) {
    if (key.length < 4) continue;
    const d = distance(p, key);
    if (!best || d < best.d) best = { target, d };
  }
  if (!best) return null;
  const allowed = p.length <= 6 ? 1 : p.length <= 12 ? 2 : 3;
  return best.d <= allowed ? best.target : null;
}
